import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CaretLeft, CaretRight } from '@phosphor-icons/react';

// Full-size the common image URL shapes we use: local uploads are already
// full-size; Unsplash thumbs get their crop/size params widened.
export function fullSize(src) {
  if (!src) return src;
  if (src.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  if (src.includes('images.unsplash.com')) return src.replace(/([?&])w=\d+/g, '$1w=1600').replace(/&h=\d+/g, '').replace(/&fit=crop/g, '');
  return src;
}

// Full-screen photo viewer. images: string[], startIndex: number, onClose: fn
export default function Lightbox({ images, startIndex = 0, onClose, alt = 'Photo' }) {
  const [index, setIndex] = useState(startIndex);
  const many = images.length > 1;
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && many) prev();
      else if (e.key === 'ArrowRight' && many) next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [many]);

  if (!images.length) return null;

  // Portal to <body>: ancestors with transforms (fade-up animations) would
  // otherwise trap position:fixed and render the overlay inside the card.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-foreground/90 backdrop-blur-sm flex items-center justify-center fade-up"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} — photo viewer`}
      data-testid="lightbox"
    >
      <button
        onClick={onClose}
        aria-label="Close photo viewer"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-background/15 hover:bg-background/30 text-background flex items-center justify-center btn-anim cursor-pointer"
        data-testid="lightbox-close"
      >
        <X size={20} weight="bold" />
      </button>

      {many && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous photo"
          className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-background/15 hover:bg-background/30 text-background flex items-center justify-center btn-anim cursor-pointer"
          data-testid="lightbox-prev"
        >
          <CaretLeft size={22} weight="bold" />
        </button>
      )}

      <img
        src={fullSize(images[index])}
        alt={`${alt} ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[86vh] object-contain rounded-xl shadow-lift"
        data-testid="lightbox-image"
      />

      {many && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next photo"
          className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-background/15 hover:bg-background/30 text-background flex items-center justify-center btn-anim cursor-pointer"
          data-testid="lightbox-next"
        >
          <CaretRight size={22} weight="bold" />
        </button>
      )}

      {many && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-background/15 text-background text-xs font-medium px-3.5 py-1.5 rounded-full" data-testid="lightbox-counter">
          {index + 1} / {images.length}
        </div>
      )}
    </div>,
    document.body
  );
}
